import { z } from "zod";

export const RESERVED_USERNAMES = [
  "admin",
  "support",
  "help",
  "mod",
  "moderator",
  "plotlounge",
  "system",
  "official",
  "staff",
  "api",
  "anonymous",
  "unknown",
] as const;

export const usernameSchema = z
  .string()
  .min(3, "Username must be at least 3 characters")
  .max(30, "Username must be 30 characters or less")
  .regex(
    /^[a-zA-Z][a-zA-Z0-9_]*$/,
    "Must start with a letter, only letters/numbers/underscores",
  )
  .refine(
    (val) =>
      !RESERVED_USERNAMES.includes(
        val.toLowerCase() as (typeof RESERVED_USERNAMES)[number],
      ),
    "This username is reserved",
  );

export const updateProfileSchema = z.object({
  username: usernameSchema.optional(),
  displayName: z
    .string()
    .min(1, "Display name is required")
    .max(100, "Display name must be 100 characters or less")
    .optional(),
  bio: z.string().max(500, "Bio must be 500 characters or less").optional(),
});

export type UpdateProfileInput = z.infer<typeof updateProfileSchema>;

// Theme preference validation
export const themePreferenceSchema = z.enum(["light", "dark", "system"]);

export const updateThemePreferenceSchema = z.object({
  themePreference: themePreferenceSchema,
});

export type ThemePreference = z.infer<typeof themePreferenceSchema>;
export type UpdateThemePreferenceInput = z.infer<
  typeof updateThemePreferenceSchema
>;

// Locale preference validation
export const localePreferenceSchema = z.enum(["en", "de", "pcm"]);

export const updateLocalePreferenceSchema = z.object({
  localePreference: localePreferenceSchema,
});

export type LocalePreference = z.infer<typeof localePreferenceSchema>;
export type UpdateLocalePreferenceInput = z.infer<
  typeof updateLocalePreferenceSchema
>;

// Plotlounge visibility toggle (Phase 6)
export const updatePlotloungeVisibilitySchema = z.object({
  showPlotloungeRooms: z.boolean(),
});

export type UpdatePlotloungeVisibilityInput = z.infer<
  typeof updatePlotloungeVisibilitySchema
>;

// Favorite books validation
export const favoriteBookInputSchema = z.object({
  title: z.string().min(1),
  author: z.string().min(1),
  coverUrl: z.string().url().nullable().optional(),
  openLibraryId: z.string().nullable().optional(),
  googleBooksId: z.string().nullable().optional(),
});

export const updateFavoriteBooksSchema = z.object({
  books: z.array(favoriteBookInputSchema).max(3),
});

export type FavoriteBookInput = z.infer<typeof favoriteBookInputSchema>;
export type UpdateFavoriteBooksInput = z.infer<
  typeof updateFavoriteBooksSchema
>;
